/* eslint-disable no-shadow, no-param-reassign, no-restricted-syntax */
import React, { useEffect, useState } from 'react';
import { Button, Spinner } from '@iq/react-components';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { setActiveComponentId } from '../../bundles/application';
import { getActiveSite } from '../../bundles/sites';
import {
  getProject,
  downloadMimsConfig,
  downloadEcConfig,
  fetchProjectAction,
} from '../../bundles/projects';
import Sidebar from '../Sidebar';
import PageHeader from '../PageHeader';
import HeaderSettings from '../PageHeader/HeaderSettings';
import Notifications from '../Notifications';
import FoldableTreeView from './TreeView';
import { deepMerge, isEnergyConnectMimsProject } from '../../utils';

// Build EC Model
const buildEcModel = (ecModelData) => {
  if (!ecModelData?.ecItems) return null;

  const ecList = [];

  // Process EC items
  ecModelData.ecItems.forEach((item) => {
    const name = `${item.name} (${item.itemDesignation})`;

    const varList = [];
    const propList = [];

    // Process properties
    for (const [key, value] of Object.entries(item.properties || {})) {
      const propId = `${item.structure}.properties.${key}`;
      propList.push({
        name: key,
        id: propId,
        metadata: { id: propId },
        children: [
          {
            name: `Description: ${value.description}`,
            id: `${propId}.description`,
            metadata: { id: `${propId}.description` },
          },
          {
            name: `Data Type: ${value.ecDataType}`,
            id: `${propId}.ecDataType`,
            metadata: { id: `${propId}.ecDataType` },
          },
        ],
      });
    }

    const properties =
      propList.length > 0
        ? [
            {
              name: 'Properties',
              id: `${item.structure}.properties`,
              metadata: { id: `${item.structure}.properties` },
              children: propList,
            },
          ]
        : [];

    // Process variables
    for (const [key, value] of Object.entries(item.variables || {})) {
      const varId = `${item.structure}.variables.${key}`;
      varList.push({
        name: key,
        id: varId,
        metadata: { id: varId },
        children: [
          {
            name: `Description: ${value.description}`,
            id: `${varId}.description`,
            metadata: { id: `${varId}.description` },
          },
          {
            name: `Data Type: ${value.ecDataType}`,
            id: `${varId}.ecDataType`,
            metadata: { id: `${varId}.ecDataType` },
          },
          { name: `Unit: ${value.unit}`, id: `${varId}.unit`, metadata: { id: `${varId}.unit` } },
        ],
      });
    }

    const variables =
      varList.length > 0
        ? [
            {
              name: 'Variables',
              id: `${item.structure}.variables`,
              metadata: { id: `${item.structure}.variables` },
              children: varList,
            },
          ]
        : [];

    const obj = {
      name,
      id: item.structure,
      metadata: { id: item.structure },
      key: item.name,
      parent: item.parent,
      children: variables.concat(properties),
    };

    ecList.push(obj);
  });

  // Arrange items in hierarchy
  const itemList = [];
  const itemMap = {}; // Map of key -> item for quick lookup

  // First pass: create a map of all items by their key
  ecList.forEach((item) => {
    itemMap[item.key] = item;
  });

  // Second pass: arrange items in hierarchy
  ecList.forEach((item) => {
    if (!item.parent) {
      // Top-level item
      itemList.push(item);
    } else {
      // Find parent and add as child
      const parentItem = itemMap[item.parent];
      if (parentItem) {
        parentItem.children.push(item);
      } else {
        // Parent not found - add to top level as fallback
        console.warn(
          `Parent "${item.parent}" not found for item "${item.key}". Adding to top level.`
        );
        itemList.push(item);
      }
    }
  });

  // Create final EC model
  const { tenantId } = ecModelData;
  const envId = 'EC-ENV';
  const tenantNodeId = `${envId}.Tenant-${tenantId}`;

  return {
    name: '',
    id: 'root-ec',
    metadata: { id: 'root-ec' },
    children: [
      {
        name: ecModelData.environment,
        id: envId,
        metadata: { id: envId },
        children: [
          {
            name: `Tenant (${tenantId})`,
            id: tenantNodeId,
            metadata: { id: tenantNodeId },
            children: itemList,
          },
        ],
      },
    ],
  };
};

const BaseViewOverview = ({ isTenantAdminView = false, noSiteView = false, className = '' }) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const params = useParams();
  const { projectId } = params;

  const project = useSelector((state) => getProject(state, projectId));

  // State declarations - ALL hooks must be declared before any conditional logic
  const [isLoading, setIsLoading] = useState(false);
  const [machExpandedIds, setMachExpandedIds] = useState([]);
  const [mimsExpandedIds, setMimsExpandedIds] = useState([]);
  const [ecExpandedIds, setEcExpandedIds] = useState([]);
  const [selectedMachIds, setSelectedMachIds] = useState({});
  const [selectedMimsIds, setSelectedMimsIds] = useState({});
  const [selectedEcIds, setSelectedEcIds] = useState({});
  const [nodeMappings, setNodeMappings] = useState({
    machToMims: {},
    machToEc: {},
    mimsToMach: {},
    ecToMach: {},
  });
  const [machModel, setMachModel] = useState(null);
  const [mimsModel, setMimsModel] = useState(null);
  const [ecModel, setEcModel] = useState(null);
  const [projectIsEc, setProjectIsEc] = useState(false);

  // Fetch project effect
  useEffect(() => {
    if (project) return;
    dispatch(fetchProjectAction(projectId));
  }, [dispatch, projectId, project]);

  // Process project data and build models
  useEffect(() => {
    if (!project) return;

    // Check if project is EC
    const isEc = isEnergyConnectMimsProject(project);
    setProjectIsEc(isEc);

    // Build mappings
    const machToMims = {};
    const machToEc = {};
    const mimsToMach = {};
    const ecToMach = {};

    const parseGroups = (computer, group) => {
      group.groupItems.forEach((item) => {
        const machId = `${computer.name}.${group.name}.${item.name}`;

        if (item.mimsEquipmentStructure && item.mimsEquipmentPropertyName) {
          const mimsId = `${item.mimsEquipmentStructure}.${item.mimsEquipmentPropertyName}`;
          machToMims[machId] = mimsId;
          mimsToMach[mimsId] = machId;
        }

        if (item.ecItemStructure && item.ecItemVariableName) {
          // FIX: Determine if it's a property or variable
          const ecItem = project?.ecModel?.ecItems?.find(
            (ec) => ec.structure === item.ecItemStructure
          );

          let ecId;
          if (ecItem) {
            if (ecItem.properties && ecItem.properties[item.ecItemVariableName]) {
              ecId = `${item.ecItemStructure}.properties.${item.ecItemVariableName}`;
            } else if (ecItem.variables && ecItem.variables[item.ecItemVariableName]) {
              ecId = `${item.ecItemStructure}.variables.${item.ecItemVariableName}`;
            } else {
              ecId = `${item.ecItemStructure}.variables.${item.ecItemVariableName}`;
            }
          } else {
            ecId = `${item.ecItemStructure}.variables.${item.ecItemVariableName}`;
          }

          machToEc[machId] = ecId;
          ecToMach[ecId] = machId;
        }
      });
    };

    // Process computers to build mappings
    if (project.machComputers) {
      project.machComputers.forEach((computer) => {
        if (computer.groups) {
          computer.groups.forEach((group) => {
            if (group.groupItems) {
              parseGroups(computer, group);
            }
          });
        }
      });
    }

    // Helper functions
    const expand = (str, value) => {
      const items = str.split('.');
      const output = {};
      let ref = output;

      items.slice(0, -1).forEach((item) => {
        ref[item] = {};
        ref = ref[item];
      });

      ref[items[items.length - 1]] = value;

      return output;
    };

    const convertToTreeFormat = (item, parentId) => {
      if (!item) return [];
      return Object.entries(item).map(([key, value]) => {
        let id;
        let children;
        if (value instanceof Object) {
          id = value.id;
          delete value.id;
          children = convertToTreeFormat(value, id);
        } else {
          id = `${parentId}.${key}`;
          children = [{ name: value, metadata: { id: `${id}.value` } }];
        }

        return {
          name: key,
          id,
          metadata: { id },
          children,
        };
      });
    };

    const convertLinkIds = (id) => {
      if (!id) return [];

      let parent;
      const output = [];

      id.split('.').forEach((item) => {
        parent = parent ? `${parent}.${item}` : item;
        output.push(parent);
      });

      return output;
    };

    setNodeMappings({ machToMims, machToEc, mimsToMach, ecToMach });

    const parseGroupItems = (c, g, gi) => {
      const mimsId =
        gi.mimsEquipmentStructure && gi.mimsEquipmentPropertyName
          ? `${gi.mimsEquipmentStructure}.${gi.mimsEquipmentPropertyName}`
          : null;
      let ecId = null;
      if (gi.ecItemStructure && gi.ecItemVariableName) {
        const ecItem = project?.ecModel?.ecItems?.find(
          (item) => item.structure === gi.ecItemStructure
        );

        if (ecItem) {
          // Check if it exists in properties
          if (ecItem.properties && ecItem.properties[gi.ecItemVariableName]) {
            ecId = `${gi.ecItemStructure}.properties.${gi.ecItemVariableName}`;
          }
          // Check if it exists in variables
          else if (ecItem.variables && ecItem.variables[gi.ecItemVariableName]) {
            ecId = `${gi.ecItemStructure}.variables.${gi.ecItemVariableName}`;
          }
          // Fallback - assume it's a variable if not found
          else {
            ecId = `${gi.ecItemStructure}.variables.${gi.ecItemVariableName}`;
          }
        } else {
          // If EC item not found, default to variables
          ecId = `${gi.ecItemStructure}.variables.${gi.ecItemVariableName}`;
        }
      }

      const machId = `${c.name}.${g.name}.${gi.name}`;

      // Use valid IDs for expansion
      const expandMach = [machId.split('.')[0], `${c.name}.${g.name}`, machId];
      const expandMims = mimsId ? convertLinkIds(mimsId) : [];
      const expandEc = ecId ? convertLinkIds(ecId) : [];

      return {
        name: gi.name,
        id: machId,
        metadata: {
          id: machId,
          expandMach,
          expandMims,
          expandEc,
          mimsId,
          ecId,
        },
      };
    };

    const MachGroupItems = (c, g) =>
      (g.groupItems || []).map((gi) => {
        return parseGroupItems(c, g, gi);
      });
    const MachChildrenMapper = (c) => {
      return c.groups.map((g) => ({
        name: g.name,
        id: `${c.name}.${g.name}`,
        metadata: { id: `${c.name}.${g.name}` },
        children: MachGroupItems(c, g),
      }));
    };
    const convertMachComputers = (computers) => {
      return computers.map((c) => ({
        name: c.name,
        id: c.name,
        metadata: { id: c.name },
        children: MachChildrenMapper(c),
      }));
    };

    const convertMimsEquipment = (equipments) => {
      const propertyReducer = (obj, item) => ({
        ...obj,
        [item.name]: item.machDataType,
      });

      const result = equipments
        .map((e) => {
          const properties = e.properties.reduce(propertyReducer, {});
          return expand(e.structure, { ...properties, id: e.structure });
        })
        .reverse()
        .reduce((accumulator, item) => deepMerge(accumulator, item), {});

      return convertToTreeFormat(result);
    };

    // Build MACH Model
    const convertedComputers = convertMachComputers(project.machComputers || []);
    const mModel = {
      name: '',
      id: 'mach-root',
      metadata: { id: 'mach-root' },
      children: [
        {
          name: project.name,
          id: 'mach-project',
          metadata: { id: 'mach-project' },
          children: convertedComputers,
        },
      ],
    };
    setMachModel(mModel);

    // Build MIMS Model
    const convertedEquipments = convertMimsEquipment(project.mimsEquipment || []);
    const mimModel = {
      name: '',
      id: 'mims-root',
      metadata: { id: 'mims-root' },
      children: [
        {
          name: `${project.name} (${project.ip})`,
          id: 'mims-project',
          metadata: { id: 'mims-project' },
          children: convertedEquipments,
        },
      ],
    };
    setMimsModel(mimModel);

    // Build EC Model if needed
    if (isEc && project.ecModel) {
      const ecTreeModel = buildEcModel(project.ecModel);
      setEcModel(ecTreeModel);
    } else {
      setEcModel(null);
    }
  }, [project]);

  // Initialize EC expanded IDs
  useEffect(() => {
    if (project && projectIsEc && project.ecModel) {
      setEcExpandedIds([1, 'EC-ENV', `EC-ENV.Tenant-${project.ecModel.tenantId}`]);
    }
  }, [project, projectIsEc]);

  const handleMachNodeSelect = (machId, newMachIds, newMimsIds, newEcIds) => {
    newMachIds[machId] = true;

    const correspondingMimsId = nodeMappings.machToMims[machId];
    const correspondingEcId = nodeMappings.machToEc[machId];

    if (correspondingMimsId) {
      newMimsIds[correspondingMimsId] = true;

      // Just pass the target MIMS ID - the TreeView will handle finding ancestors
      setMimsExpandedIds((prevIds) => {
        const uniqueIds = new Set([...prevIds, correspondingMimsId]);
        return Array.from(uniqueIds);
      });
    }

    if (correspondingEcId && ecModel) {
      newEcIds[correspondingEcId] = true;

      // Just pass the target EC ID - the TreeView will handle finding ancestors
      setEcExpandedIds((prevIds) => {
        const uniqueIds = new Set([...prevIds, correspondingEcId]);
        return Array.from(uniqueIds);
      });
    }
  };

  const handleMimsNodeSelect = (mimsId, newMachIds, newMimsIds, newEcIds) => {
    newMimsIds[mimsId] = true;

    // Just pass the target MIMS ID - the TreeView will handle finding ancestors
    setMimsExpandedIds((prevIds) => {
      const uniqueIds = new Set([...prevIds, mimsId]);
      return Array.from(uniqueIds);
    });

    const correspondingMachId = nodeMappings.mimsToMach[mimsId];
    if (correspondingMachId) {
      newMachIds[correspondingMachId] = true;

      const correspondingEcId = nodeMappings.machToEc[correspondingMachId];
      if (correspondingEcId && ecModel) {
        newEcIds[correspondingEcId] = true;

        // Just pass the target EC ID - the TreeView will handle finding ancestors
        setEcExpandedIds((prevIds) => {
          const uniqueIds = new Set([...prevIds, correspondingEcId]);
          return Array.from(uniqueIds);
        });
      }
    }
  };

  const handleEcNodeSelect = (ecId, newMachIds, newMimsIds, newEcIds) => {
    newEcIds[ecId] = true;

    // Just pass the target EC ID - the TreeView will handle finding ancestors
    setEcExpandedIds((prevIds) => {
      const uniqueIds = new Set([...prevIds, ecId]);
      return Array.from(uniqueIds);
    });

    const correspondingMachId = nodeMappings.ecToMach[ecId];
    if (correspondingMachId) {
      newMachIds[correspondingMachId] = true;

      const correspondingMimsId = nodeMappings.machToMims[correspondingMachId];
      if (correspondingMimsId) {
        newMimsIds[correspondingMimsId] = true;

        // Just pass the target MIMS ID - the TreeView will handle finding ancestors
        setMimsExpandedIds((prevIds) => {
          const uniqueIds = new Set([...prevIds, correspondingMimsId]);
          return Array.from(uniqueIds);
        });
      }
    }
  };

  const handleNodeSelect = (mappingData) => {
    if (!mappingData) return;

    const { machId, mimsId, ecId } = mappingData;

    // Create a new selection state for each tree
    const newMachIds = {};
    const newMimsIds = {};
    const newEcIds = {};

    // Handle MACH node selection
    if (machId) {
      handleMachNodeSelect(machId, newMachIds, newMimsIds, newEcIds);
    }
    // Handle MIMS node selection
    else if (mimsId) {
      handleMimsNodeSelect(mimsId, newMachIds, newMimsIds, newEcIds);
    }
    // Handle EC node selection
    else if (ecId) {
      handleEcNodeSelect(ecId, newMachIds, newMimsIds, newEcIds);
    }

    // Update selection states
    setSelectedMachIds(newMachIds);
    setSelectedMimsIds(newMimsIds);
    setSelectedEcIds(newEcIds);
  };

  const onResetComponent = () => dispatch(setActiveComponentId(null));

  const handleClickDownloadMims = () => {
    setIsLoading(true);
    setTimeout(() => {
      dispatch(downloadMimsConfig(project));
      setIsLoading(false);
    }, 2000);
  };

  const handleClickDownloadEc = () => {
    setIsLoading(true);
    setTimeout(() => {
      dispatch(downloadEcConfig(project));
      setIsLoading(false);
    }, 2000);
  };

  if (!project) return null;

  // Render component
  return (
    <>
      <Sidebar
        projectId={projectId}
        ecProject={project.ecModel}
      />
      <section className={`base-view ${className}`}>
        <PageHeader
          site={!isTenantAdminView && site}
          title={{
            type: 'string',
            value: project.name,
          }}
          singlePage={true}
          isTenantAdminView={isTenantAdminView}
          dispatchResetComponent={onResetComponent}
        >
          <Notifications />

          <HeaderSettings
            site={site}
            noSiteView={noSiteView}
          />
        </PageHeader>
        <div className="panel">
          <div className="panel-header">
            <span className="iot-class">Overview</span>
            <div className="panel-header-button">
              {isLoading && <Spinner />}
              <Button onClick={handleClickDownloadMims}>Download MIMS</Button>
              {projectIsEc && <Button onClick={handleClickDownloadEc}>Download EC</Button>}
            </div>
          </div>

          <div className="data-container">
            {machModel && (
              <div className="column-wrapper">
                <header className="title">MACH</header>
                <div className="column mach-data">
                  <FoldableTreeView
                    data={machModel}
                    expandedIds={machExpandedIds}
                    selectedIds={selectedMachIds}
                    setMachExpandedIds={setMachExpandedIds}
                    setMimsExpandedIds={setMimsExpandedIds}
                    setEcExpandedIds={setEcExpandedIds}
                    treeType="mach"
                    onNodeSelect={handleNodeSelect}
                  />
                </div>
              </div>
            )}

            {mimsModel && (
              <div className="column-wrapper">
                <header className="title">MIMS</header>
                <div className="column mims-data">
                  <FoldableTreeView
                    data={mimsModel}
                    expandedIds={mimsExpandedIds}
                    selectedIds={selectedMimsIds}
                    treeType="mims"
                    onNodeSelect={handleNodeSelect}
                    setMachExpandedIds={setMachExpandedIds}
                    setMimsExpandedIds={setMimsExpandedIds}
                    setEcExpandedIds={setEcExpandedIds}
                  />
                </div>
              </div>
            )}

            {projectIsEc && ecModel && (
              <div className="column-wrapper">
                <header className="title">EC</header>
                <div className="column dec-data">
                  <FoldableTreeView
                    data={ecModel}
                    expandedIds={ecExpandedIds}
                    selectedIds={selectedEcIds}
                    treeType="ec"
                    onNodeSelect={handleNodeSelect}
                    setMachExpandedIds={setMachExpandedIds}
                    setMimsExpandedIds={setMimsExpandedIds}
                    setEcExpandedIds={setEcExpandedIds}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default BaseViewOverview;
